import { spawn, type ChildProcessWithoutNullStreams } from 'node:child_process';
import { existsSync } from 'node:fs';
import { dirname, resolve } from 'node:path';
import { fileURLToPath } from 'node:url';

export interface ITurnEmitter {
  onPartial(cb: (text: string) => void): void;
  onCommitted(cb: (text: string) => void): void;
  onError(cb: (error: Error) => void): void;
  sendAudio(pcm: Buffer): boolean;
  /** False once the underlying engine process has exited or been closed. */
  isAlive(): boolean;
  /** Most recent error reported by the engine (e.g. a startup crash), if any. */
  getLastError(): string | null;
  stop(): Promise<void>;
  close(): void;
}

type SidecarMessage =
  | { type: 'ready' }
  | { type: 'transcript:partial'; text: string }
  | { type: 'transcript:committed'; text: string }
  | { type: 'transcript:finalized' }
  | { type: 'error'; error: string };

const SAMPLE_RATE = 16000;
const MAX_BACKEND_AUDIO_BUFFER_BYTES = 1_000_000;

function resolveSidecarBinary(): string {
  const here = dirname(fileURLToPath(import.meta.url));
  const candidates = [
    resolve(here, '../../packages/moonshine-linux-x64/bin/moonshine-sidecar'),
    resolve(process.cwd(), 'packages/moonshine-linux-x64/bin/moonshine-sidecar'),
    resolve(process.cwd(), 'node_modules/moonshine-linux-x64/bin/moonshine-sidecar'),
  ];
  const binary = candidates.find((candidate) => existsSync(candidate));
  if (!binary) {
    throw new Error(`Moonshine sidecar binary not found. Run npm run build:sidecar to create ${candidates[0]}`);
  }
  return binary;
}

class MoonshineTranscription implements ITurnEmitter {
  private readonly child: ChildProcessWithoutNullStreams;
  private readonly partialCallbacks = new Set<(text: string) => void>();
  private readonly committedCallbacks = new Set<(text: string) => void>();
  private readonly errorCallbacks = new Set<(error: Error) => void>();
  private stdoutBuffer = '';
  private closed = false;
  private stopping = false;
  private exited = false;
  private lastError: string | null = null;
  private stopResolver: (() => void) | null = null;

  constructor(modelSize: string) {
    this.child = spawn(resolveSidecarBinary(), [], {
      env: {
        ...process.env,
        MOONSHINE_MODEL: modelSize,
      },
      stdio: ['pipe', 'pipe', 'pipe'],
    });

    this.child.stdout.setEncoding('utf8');
    this.child.stdout.on('data', (chunk) => this.handleStdout(chunk));
    this.child.stderr.setEncoding('utf8');
    this.child.stderr.on('data', (chunk) => this.emitError(new Error(String(chunk).trim())));
    this.child.on('error', (error) => this.emitError(error));
    this.child.on('exit', (code, signal) => {
      this.exited = true;
      if (!this.closed && !this.stopping) {
        this.emitError(new Error(`Moonshine sidecar exited unexpectedly (code=${code ?? 'null'}, signal=${signal ?? 'null'})`));
      }
    });
  }

  isAlive(): boolean {
    return !this.closed && !this.exited && !this.child.stdin.destroyed;
  }

  getLastError(): string | null {
    return this.lastError;
  }

  onPartial(cb: (text: string) => void): void {
    this.partialCallbacks.add(cb);
  }

  onCommitted(cb: (text: string) => void): void {
    this.committedCallbacks.add(cb);
  }

  onError(cb: (error: Error) => void): void {
    this.errorCallbacks.add(cb);
  }

  sendAudio(pcm: Buffer): boolean {
    return this.writeJson({
      type: 'audio',
      encoding: 'pcm16le',
      sampleRate: SAMPLE_RATE,
      audio: pcm.toString('base64'),
    });
  }

  stop(): Promise<void> {
    this.stopping = true;
    if (!this.writeJson({ type: 'stop' })) return Promise.resolve();
    return new Promise((resolve) => {
      this.stopResolver = resolve;
    });
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.writeJson({ type: 'close' });
    this.child.kill('SIGTERM');
    this.stopResolver?.();
    this.stopResolver = null;
    this.partialCallbacks.clear();
    this.committedCallbacks.clear();
    this.errorCallbacks.clear();
  }

  private handleStdout(chunk: string): void {
    this.stdoutBuffer += chunk;
    let newline = this.stdoutBuffer.indexOf('\n');
    while (newline !== -1) {
      const line = this.stdoutBuffer.slice(0, newline).trim();
      this.stdoutBuffer = this.stdoutBuffer.slice(newline + 1);
      if (line) this.handleMessage(line);
      newline = this.stdoutBuffer.indexOf('\n');
    }
  }

  private handleMessage(line: string): void {
    let message: SidecarMessage;
    try {
      message = JSON.parse(line) as SidecarMessage;
    } catch (error) {
      this.emitError(error instanceof Error ? error : new Error(String(error)));
      return;
    }

    if (message.type === 'transcript:partial') {
      for (const cb of this.partialCallbacks) cb(message.text);
    } else if (message.type === 'transcript:committed') {
      for (const cb of this.committedCallbacks) cb(message.text);
    } else if (message.type === 'transcript:finalized') {
      this.stopResolver?.();
      this.stopResolver = null;
    } else if (message.type === 'error') {
      this.emitError(new Error(message.error));
    }
  }

  private writeJson(message: Record<string, unknown>): boolean {
    if (this.closed || this.child.stdin.destroyed) return false;
    const payload = `${JSON.stringify(message)}\n`;
    if (this.child.stdin.writableLength + Buffer.byteLength(payload) > MAX_BACKEND_AUDIO_BUFFER_BYTES) {
      return false;
    }
    return this.child.stdin.write(payload);
  }

  private emitError(error: Error): void {
    this.lastError = error.message;
    for (const cb of this.errorCallbacks) cb(error);
  }
}

export function createMoonshineTranscription(modelSize: string): ITurnEmitter {
  return new MoonshineTranscription(modelSize);
}
